import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { DeckThread } from '~/deck15/chat/DeckThread';
import { ArtifactTileMorph } from '~/deck15/directory/ArtifactTileMorph';
import { useArtifactLayout } from '~/deck15/directory/useArtifactLayout';
import { genBegin, genClear, genPhase, genRecord, genResolve } from '~/deck15/generate/genBus';
import { useGenRows } from '~/deck15/generate/useGenRows';
import { itemKey, parseItemKey } from '~/deck15/ids';
import { useGenMorph } from '~/deck15/preview/useStageForX';
import { Composer } from '~/design-system/components/v1_5/Chat';
import { DirectoryPanel, type DirectoryItem } from '~/design-system/components/v1_5/Directory';
import { HeaderV15 } from '~/design-system/components/v1_5/Header';
import { LayoutShell, PanelHeader } from '~/design-system/components/v1_5/Layout';
import type { HeaderTheme } from '~/design-system/components/v1_5/Layout/theme';
import { PreviewBarButton, PreviewPane } from '~/design-system/components/v1_5/Preview';
import { usePrefersReducedMotion } from '~/landing_page/components/FeaturesSection/usePrefersReducedMotion';
import { formatVersionTimestamp } from '~/utils/date-utils';
import { buildThread, LIVE_ID, liveBlocksAt, REPLAY_PHASES, timelineOf, type Cue } from './demoThread';
import { HERO_SCRIPTS, type HeroDemoScript } from './heroScripts';
import { slideTexture, textureFor, thumbnailFor } from './slideRaster';
import { slideThumbnail, type DemoSlide } from './slideSvg';

/**
 * The Deck 1.5 workspace writing one hero use-case's deck, scripted (`heroScripts.ts`).
 *
 * Every surface is the workspace's own, fed on a clock instead of a stream. The thread is `DeckThread`
 * over stored messages plus the live turn's blocks. The directory is `DirectoryPanel` through
 * `useGenRows`, so a row being written reads `writing · <phase>` and its tile plays `ArtifactTileMorph`.
 * The preview follows the agent onto each artifact as it starts and draws `useGenMorph`'s band and
 * resolve. The generation state they all read is the real `genBus`, driven the way `agentStream` drives
 * it when the agent's `create_artifact` replays a document onto the workspace: one artifact at a time.
 */

const MODEL = { vendor: 'google', label: 'Gemini 3.8 Flash' } as const;
/** How long the finished deck holds before the demo goes round again. */
const HOLD_MS = 9000;
/** `useGenMorph`'s resolve plus its linger: the record is cleared after this if nothing else did. */
const RESOLVE_CLEAR_MS = 2550 + 600;
const TICK_MS = 100;

/* The agent pane's own layout (`DeckAgentPane`): a full-height scroller that never scrolls sideways. */
const AGENT_STAGE: CSSProperties = { flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 12px 12px', position: 'relative' };
const AGENT_SCROLL: CSSProperties = {
  flex: '1 1 0%',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingInline: 6,
  marginInline: -6,
  scrollbarWidth: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
};
const AGENT_TAIL: CSSProperties = { width: '100%', boxSizing: 'border-box', paddingInline: 8, paddingBlock: 12 };
const AGENT_COMPOSER: CSSProperties = { width: '100%', marginInline: 'auto', position: 'relative' };
/** `ConversationColumn`'s cap, opted out of the way the pane does: the pane is the measure. */
const UNCAPPED = 100000;
/** The pane's follow rule: within this of the end the reader is following, further and they have scrolled away. */
const FOLLOW_SLACK = 120;

const noop = () => {};

/** The slide, inline, so the page's Inter applies. */
function Slide({ slide }: { slide: DemoSlide }) {
  return <div style={{ width: '100%', height: '100%', background: '#fff' }} dangerouslySetInnerHTML={{ __html: slide.svg }} />;
}

/** Numeric artifact ids, one block per script, so two demos never share a generation record. */
function idsFor(script: HeroDemoScript): number[] {
  const base = 90_000 + Math.max(0, HERO_SCRIPTS.findIndex((s) => s.id === script.id)) * 100;
  return script.slides.map((_, i) => base + i + 1);
}

/** How long a tab takes to write its deck and hold it: the hero's dwell for that tab. */
export function demoDurationMs(script: HeroDemoScript): number {
  return timelineOf(script.live).endAt + 6000;
}

export interface HeroPitchDemoProps {
  script: HeroDemoScript;
  theme?: HeaderTheme;
}

export function HeroPitchDemo({ script, theme = 'dark' }: HeroPitchDemoProps) {
  const slides = script.slides;
  const reducedMotion = usePrefersReducedMotion();
  const timeline = useMemo(() => timelineOf(script.live), [script]);
  const ids = useMemo(() => idsFor(script), [script]);
  const [t, setT] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [agentOpen, setAgentOpen] = useState(true);
  const [directoryOpen, setDirectoryOpen] = useState(true);
  const [artifactLayout, setArtifactLayout] = useArtifactLayout();
  const [openedAt] = useState(() => Date.now());
  const stageRef = useRef<HTMLDivElement | null>(null);

  // Each artifact rasterised once, ahead of its turn: the texture the morph resolves onto and the
  // directory's thumbnail. The count re-renders the rows as each raster lands.
  const [, setRasterized] = useState(0);
  useEffect(() => {
    let alive = true;
    slides.forEach((slide) => {
      void slideTexture(slide.id, slide.svg).then(() => alive && setRasterized((n) => n + 1));
    });
    return () => {
      alive = false;
    };
  }, [slides]);

  // The clock, and the generation it drives. One chain of timers torn down whole; under reduced motion
  // the finished deck is shown and nothing moves.
  useEffect(() => {
    if (reducedMotion) {
      setT(timeline.endAt);
      setSelected(ids[ids.length - 1] ?? null);
      return;
    }
    let timers: number[] = [];
    let tick = 0;
    let cancelled = false;
    const at = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(() => !cancelled && fn(), ms));
    };
    const clearRecords = () => ids.forEach((id) => genRecord(id) && genClear(id));

    const run = () => {
      timers.forEach(clearTimeout);
      timers = [];
      window.clearInterval(tick);
      clearRecords();
      setSelected(null);
      const startedAt = performance.now();
      setT(0);
      tick = window.setInterval(() => setT(Math.min(performance.now() - startedAt, timeline.endAt)), TICK_MS);

      timeline.cues.forEach((cue) => {
        if (cue.kind !== 'artifact') return;
        const id = ids[cue.index];
        const slide = slides[cue.index];
        const replay = cue.doneAt - cue.at;
        at(cue.at, () => {
          // `create_artifact` has run: the row exists, the preview follows the agent onto it.
          genBegin(id, `Write ${slide.title}`, null, 'generate');
          setSelected(id);
        });
        REPLAY_PHASES.forEach((phase, k) => at(cue.at + ((k + 1) * replay) / (REPLAY_PHASES.length + 1), () => genPhase(id, phase)));
        at(cue.doneAt, () => {
          const tex = textureFor(slide.id);
          genResolve(id, tex?.canvas ?? null, tex?.parts ?? null);
        });
        at(cue.doneAt + RESOLVE_CLEAR_MS, () => genRecord(id) && genClear(id));
      });
      at(timeline.endAt, () => window.clearInterval(tick));
      at(timeline.endAt + HOLD_MS, run);
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.clearInterval(tick);
      clearRecords();
    };
  }, [reducedMotion, timeline, ids, slides]);

  const done = t >= timeline.endAt;
  const started = timeline.cues.filter((cue) => cue.at <= t);
  const artifactsStarted = started.filter((cue): cue is Extract<Cue, { kind: 'artifact' }> => cue.kind === 'artifact');
  const artifactsWritten = artifactsStarted.filter((cue) => t >= cue.doneAt).length;

  /* ── the thread ── */
  const messages = useMemo(
    () => buildThread(script.history, script.live, timeline, { openedAt, done }),
    [script, timeline, openedAt, done],
  );
  const lastCue = started[started.length - 1];
  const live = useMemo(
    () =>
      done
        ? null
        : {
            messageId: LIVE_ID,
            blocks: liveBlocksAt(script.live, timeline, t),
            elapsedSec: script.live.thoughtSec + Math.round(t / 1000),
            quietMs: lastCue ? t - lastCue.at : undefined,
          },
    [done, script, timeline, t, lastCue],
  );

  // The thread opens on its end and stays pinned to it while the reader is there, the way the pane
  // does. Pinned on every change of the turn AND on every change of height, because a line types
  // itself in between the clock's ticks; pinning only when a step starts let each line slide under the
  // composer and then jump.
  const atEnd = useRef(true);
  const tailRef = useRef<HTMLDivElement | null>(null);
  const pin = useCallback(() => {
    const stage = stageRef.current;
    if (stage && atEnd.current) stage.scrollTop = stage.scrollHeight;
  }, []);
  const onStageScroll = useCallback(() => {
    const stage = stageRef.current;
    if (stage) atEnd.current = stage.scrollHeight - stage.scrollTop - stage.clientHeight < FOLLOW_SLACK;
  }, []);
  useLayoutEffect(pin, [pin, messages, live]);
  useEffect(() => {
    const tail = tailRef.current;
    if (!tail || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(pin);
    observer.observe(tail);
    return () => observer.disconnect();
  }, [pin]);

  /* ── the directory: rows exist from the moment each artifact is created ── */
  const rows: DirectoryItem[] = useMemo(
    () =>
      artifactsStarted.map((cue) => {
        const slide = slides[cue.index];
        const written = t >= cue.doneAt;
        return {
          id: itemKey('artifacts', ids[cue.index]),
          title: slide.title,
          meta: `Artifact ${String(cue.index + 1).padStart(2, '0')} · ${formatVersionTimestamp(openedAt)}`,
          ...(written ? { thumbnail: thumbnailFor(slide.id) ?? slideThumbnail(slide) } : {}),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artifactsStarted.length, artifactsWritten, slides, ids, openedAt],
  );
  const items = useGenRows(rows);

  /* ── the preview: the open artifact, with a running generation drawn over it ── */
  const selectedIndex = selected === null ? -1 : ids.indexOf(selected);
  const selectedSlide = selectedIndex === -1 ? null : slides[selectedIndex];
  const selectedWritten = artifactsStarted.some((cue) => cue.index === selectedIndex && t >= cue.doneAt);
  const gen = useGenMorph(selected, {
    settled: selectedSlide ? <Slide slide={selectedSlide} /> : null,
    bandWhileStreaming: true,
  });
  const stage = gen.morph ?? (selectedSlide && selectedWritten ? <Slide slide={selectedSlide} /> : null);
  const status = selectedSlide ? gen.status ?? (selectedWritten ? 'ready' : 'loading') : 'empty';

  const select = useCallback((key: string) => {
    const parsed = parseItemKey(key);
    if (parsed?.kind === 'artifacts') setSelected(parsed.id);
  }, []);

  return (
    <LayoutShell
      theme={theme}
      agentOpen={agentOpen}
      directoryOpen={directoryOpen}
      header={
        <HeaderV15 theme={theme} title={script.deckTitle} userName="Guri Sitchinava" agentOn={agentOpen} onAgentToggle={() => setAgentOpen((v) => !v)} onAdd={noop} />
      }
      agent={
        <>
          <PanelHeader theme={theme}>Agent</PanelHeader>
          <div style={AGENT_STAGE}>
            <div ref={stageRef} className="deck15-thread-scroll" style={AGENT_SCROLL} onScroll={onStageScroll}>
              <div ref={tailRef} style={AGENT_TAIL}>
                <DeckThread
                  theme={theme}
                  columnWidth={UNCAPPED}
                  messages={messages}
                  live={live}
                  onOpenArtifact={select}
                />
              </div>
            </div>
            <div style={AGENT_COMPOSER}>
              <Composer
                theme={theme}
                input={{ content: 'placeholder', text: 'Share your work context…' }}
                toolbar={{ model: MODEL, send: { state: done ? 'idle' : 'stop' } }}
              />
            </div>
          </div>
        </>
      }
      directory={
        <DirectoryPanel
          theme={theme}
          kind="artifacts"
          onKindChange={noop}
          artifactLayout={artifactLayout}
          onArtifactLayoutChange={setArtifactLayout}
          renderWorking={(item) => {
            const key = parseItemKey(item.id);
            return key?.kind === 'artifacts' ? <ArtifactTileMorph slideId={key.id} /> : null;
          }}
          items={items}
          selectedId={selected === null ? null : itemKey('artifacts', selected)}
          onSelect={select}
        />
      }
      preview={
        <PreviewPane
          theme={theme}
          title={selectedSlide?.title}
          meta={selectedSlide ? `artifact ${String(selectedIndex + 1).padStart(2, '0')}${status === 'writing' || status === 'loading' ? ' · writing' : ''}` : undefined}
          status={status}
          kind="slide"
          naturalWidth={1920}
          naturalHeight={1080}
          indicator={selectedSlide ? `${selectedIndex + 1} of ${artifactsStarted.length}` : undefined}
          leading={<PreviewBarButton icon="side-bar" label="Directory" on={directoryOpen} theme={theme} onClick={() => setDirectoryOpen((v) => !v)} />}
          trailing={
            <>
              <PreviewBarButton icon="palette" label="Design" theme={theme} />
              <PreviewBarButton icon="settings" label="View" theme={theme} />
            </>
          }
        >
          {stage}
        </PreviewPane>
      }
    />
  );
}

HeroPitchDemo.displayName = 'HeroPitchDemo';
